import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useLogin } from '@/features/auth/hooks/useAuth'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useTimezone } from '@/hooks/useTimezone'
import { useAppType } from '@/hooks/useAppType'
import { cn } from '@/lib/utils'

/**
 * ONE form: the username and the password are submitted together, and nothing is asked of the
 * server until both are in hand.
 *
 * It used to take the email first, ask /auth/check-mfa what that account was configured for, and
 * change the screen accordingly — a password box for one address, an automatic passkey prompt for
 * another. Anyone could therefore learn, from the email alone, whether an address was a real
 * account and how it signs in; a disabled account even answered 401 where an unknown one answered
 * 200. Half the credential, given away before a password was typed (Andrew, 2026-09-18).
 *
 * There are no passkey-only accounts either (Andrew, 2026-09-18): a passkey is the SECOND factor,
 * so this screen has no passkey button at all. It takes the username and password, and the MFA
 * screen that follows is where a passkey or a code is presented — reachable only with the
 * short-lived token that a verified password mints.
 *
 * The two-step form had a second cost: React Hook Form keeps the value of an input whose element
 * has unmounted, so the password typed for the first attempt was still in the field on the way
 * back, masked and invisible, and the corrected attempt appended to it. One form cannot have that
 * bug — and the field is still emptied on a rejected attempt, because a refused password is never
 * the start of the next one.
 */
export function LoginPage() {
    const { t } = useTranslation()
    const { appType, isLoading: appTypeLoading } = useAppType()
    const [showPassword, setShowPassword] = useState(false)
    const { timezoneInfo } = useTimezone()
    const login = useLogin()

    const schema = z.object({
        email: z.string().email(t('auth.loginPage.invalidEmail')),
        password: z.string().min(1, t('auth.loginPage.passwordRequired')),
    })

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: { email: '', password: '' },
    })

    // Why the last attempt failed, shown on the form above the button. A wrong password is a 401 and
    // gets our own translated wording rather than the server's English; rate limiting has its own;
    // anything else says what the server said, or a general line when there is no answer at all.
    const loginError = (() => {
        if (!login.isError) return null
        const err = login.error as { response?: { status?: number; data?: { message?: string } } }
        const status = err?.response?.status
        if (status === 401) return t('auth.loginPage.invalidCredentials', 'Incorrect email or password.')
        if (status === 429) return t('auth.loginPage.tooManyAttempts', 'Too many attempts. Please wait a moment and try again.')
        if (status && status < 500 && err.response?.data?.message) return err.response.data.message
        return t('auth.loginPage.signInFailed', 'Sign in failed. Please try again.')
    })()

    const handleSubmit = (data: FormData) => {
        login.mutate(
            {
                email: data.email,
                password: data.password,
                timezone: timezoneInfo?.timezone || 'UTC',
            },
            {
                // Whatever was refused is cleared, and the cursor goes back to the field: the next
                // attempt types into an empty box rather than onto the end of the last one.
                onError: () => {
                    form.resetField('password')
                    setTimeout(() => form.setFocus('password'), 0)
                },
            },
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                <div className="flex justify-end">
                    <LanguageSwitcher />
                </div>

                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <Logo className="h-32" />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold">
                            {appTypeLoading ? '...' : t(`app.name.${appType}`)}
                        </h1>
                        <p className="text-muted-foreground">{t('auth.loginPage.title')}</p>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('auth.loginPage.signIn')}</CardTitle>
                        <CardDescription>{t('auth.loginPage.signInDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('auth.loginPage.emailLabel')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="username"
                                    placeholder={t('auth.loginPage.emailPlaceholder')}
                                    {...form.register('email')}
                                    className={cn(form.formState.errors.email && 'border-destructive')}
                                    autoFocus
                                />
                                {form.formState.errors.email && (
                                    <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">{t('auth.loginPage.passwordLabel')}</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        placeholder={t('auth.loginPage.passwordPlaceholder')}
                                        {...form.register('password')}
                                        className={cn(form.formState.errors.password && 'border-destructive')}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                                {form.formState.errors.password && (
                                    <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
                                )}
                            </div>

                            {loginError && (
                                <div
                                    role="alert"
                                    data-testid="login-error"
                                    className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                                >
                                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                                    {loginError}
                                </div>
                            )}

                            <Button type="submit" className="w-full" disabled={login.isPending}>
                                {login.isPending ? t('auth.loginPage.signingIn') : t('auth.loginPage.signInButton')}
                            </Button>
                        </form>

                    </CardContent>
                </Card>

                <div className="text-center text-sm text-muted-foreground">
                    <p>Environment: {import.meta.env.VITE_ENVIRONMENT || 'dev'}</p>
                </div>
            </div>
        </div>
    )
}
